import { useState, useEffect, MouseEvent as ReactMouseEvent } from "react";
import { useRefState } from "./use-ref-state";

export const useCtrlKeyListener = () => {
    const [isCtrlHeld, setIsCtrlHeld] = useState<boolean>(false);

    useEffect(() => {
        const onCtrlDown = (e) => (e.ctrlKey || e.metaKey) && setIsCtrlHeld(true);
        const onCtrlUp = () => setIsCtrlHeld(false);
        window.addEventListener("keydown", onCtrlDown);
        window.addEventListener("keyup", onCtrlUp);

        return () => {
            window.removeEventListener("keydown", onCtrlDown);
            window.removeEventListener("keyup", onCtrlUp);
        };
    }, []);

    return { isCtrlHeld };
};

export const useSidebarSlider = (getMinWidth: (windowWidth) => number, onResize: () => void) => {
    // Control the width of the sidebar using the below hooks
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [sliderContextState, sliderContextRef, setSliderContext] = useRefState<{ leftWidth: number; separatorXPosition: number; dragging: boolean }>({
        leftWidth: 400,
        separatorXPosition: undefined,
        dragging: false,
    });
    const minSidebarWidth = getMinWidth(windowWidth);
    const onMouseDownOnSlider = (e: ReactMouseEvent) => {
        e.preventDefault();
        setSliderContext({ ...sliderContextRef.current, separatorXPosition: e.clientX, dragging: true });

        const mouseMoveFunction = (e: MouseEvent) => {
            e.preventDefault();
            if (sliderContextRef.current.leftWidth && sliderContextRef.current.separatorXPosition) {
                const newLeftWidth = sliderContextRef.current.leftWidth + e.clientX - sliderContextRef.current.separatorXPosition;
                const pageWidth = document.body.clientWidth;
                if (newLeftWidth < minSidebarWidth) {
                    setSliderContext({ ...sliderContextRef.current, separatorXPosition: e.clientX, leftWidth: minSidebarWidth });
                } else if (newLeftWidth > pageWidth - minSidebarWidth) {
                    setSliderContext({ ...sliderContextRef.current, separatorXPosition: e.clientX, leftWidth: pageWidth - minSidebarWidth });
                } else {
                    setSliderContext({ ...sliderContextRef.current, separatorXPosition: e.clientX, leftWidth: newLeftWidth });
                }
            }
        };
        document.addEventListener("mousemove", mouseMoveFunction);

        const mouseUpFunction = () => {
            setSliderContext({ ...sliderContextRef.current, dragging: false });
            onResize();
            document.removeEventListener("mouseup", mouseUpFunction);
            document.removeEventListener("mousemove", mouseMoveFunction);
        };
        document.addEventListener("mouseup", mouseUpFunction);
        setSliderContext({ ...sliderContextRef.current, separatorXPosition: e.clientX, dragging: true });
    };

    const onDoubleClickOnSlider = () => {
        setSliderContext({ ...sliderContextRef.current, leftWidth: windowWidth - 100 });
        onResize();
    };

    const setLeftWidth = (newLeftWidth) => {
        setSliderContext({ ...sliderContextState, leftWidth: newLeftWidth });
    };
    useEffect(() => {
        // Listen to window resize to manage interface
        window.addEventListener("resize", () => setWindowWidth(window.innerWidth));
        return () => {
            window.removeEventListener("resize", () => setWindowWidth(window.innerWidth));
        };
    });

    return { windowWidth, sliderContextState, minSidebarWidth, setLeftWidth, onMouseDownOnSlider, onDoubleClickOnSlider } as const;
};
